import { useEffect, useRef, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Broadcast, MapPin, Clock, Lightning, CheckCircle, XCircle, HourglassMedium } from "@phosphor-icons/react";
import { toast } from "sonner";
import { devWarn } from "@/lib/logger";

const STATUS_META = {
  pending: { label: "Live", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  accepted: { label: "Accepted", color: "bg-blue-100 text-blue-800 border-blue-300" },
  declined: { label: "Declined", color: "bg-slate-100 text-slate-700 border-slate-300" },
  expired: { label: "Expired", color: "bg-amber-100 text-amber-800 border-amber-300" },
  cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-700 border-slate-300" },
};

function Countdown({ deadlineIso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  if (!deadlineIso) return null;
  const ms = new Date(deadlineIso).getTime() - now;
  const secs = Math.max(0, Math.ceil(ms / 1000));
  const danger = secs <= 5;
  return (
    <div data-testid="offer-countdown" className={`flex items-center gap-1.5 font-mono text-2xl font-extrabold tabular-nums ${danger ? "text-red-600 animate-pulse" : "text-slate-900"}`}>
      <HourglassMedium size={22} weight={danger ? "fill" : "regular"} />
      {secs}s
    </div>
  );
}

function OfferCard({ o, onAccept, onDecline, busy }) {
  const meta = STATUS_META[o.status] || STATUS_META.pending;
  const pending = o.status === "pending";
  return (
    <div data-testid={`offer-${o.id}`} className={`card-tactile p-5 border-l-4 ${pending ? "border-emerald-500 bg-white" : "border-slate-300 bg-slate-50"}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide rounded-full border ${meta.color}`}>{meta.label}</span>
            <span className="label-caps text-blue-600">{o.job?.category}</span>
          </div>
          <div className="font-display text-xl font-bold text-slate-900 truncate">{o.job?.title}</div>
          <div className="text-sm text-slate-600 mt-0.5">{o.job?.company_name}</div>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-700">
            <span className="flex items-center gap-1"><MapPin size={14}/> {o.job?.location} · <b>{o.distance_km} km</b></span>
            <span className="flex items-center gap-1"><Clock size={14}/> {o.job?.date} {o.job?.start_time}–{o.job?.end_time}</span>
            <span className="font-bold text-slate-900">£{o.job?.pay_rate}/hr</span>
          </div>
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-2">
            <Lightning size={12} weight="fill" className="text-amber-500" />
            Match score {(o.match_score * 100).toFixed(0)}% · Attempt #{o.attempt_num}
          </div>
        </div>
        {pending && <Countdown deadlineIso={o.expires_at} />}
      </div>
      {pending && (
        <div className="mt-4 flex gap-2">
          <Button data-testid={`offer-accept-${o.id}`} disabled={busy} onClick={() => onAccept(o)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-11">
            <CheckCircle size={18} weight="fill" className="mr-2" /> Accept
          </Button>
          <Button data-testid={`offer-decline-${o.id}`} disabled={busy} variant="outline" onClick={() => onDecline(o)} className="flex-1 h-11 border-slate-300">
            <XCircle size={18} className="mr-2" /> Decline
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [locOk, setLocOk] = useState(false);
  const esRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/staff/offers");
      setOffers(r.data || []);
    } catch (err) { devWarn("offers load", err); }
    try {
      const l = await api.get("/worker/location");
      setLocOk(!!l.data?.lat);
    } catch (err) { devWarn("worker location fetch", err); setLocOk(false); }
  }, []);

  const shareLocation = () => {
    if (!navigator.geolocation) { toast.error("Location not supported"); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await api.post("/worker/location", {
          lat: pos.coords.latitude, lon: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy, source: "live",
        });
        setLocOk(true);
        toast.success("Location shared — you'll get live offers.");
      } catch (e) { toast.error("Could not save location"); }
    }, (err) => toast.error(`Location denied: ${err.message}`), { enableHighAccuracy: true, timeout: 10000 });
  };

  useEffect(() => {
    load();
  }, [load]);

  // SSE subscription — same-origin, cookies flow automatically
  useEffect(() => {
    let closed = false;
    const url = `${API}/staff/offers/stream`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;
    es.addEventListener("hello", () => setConnected(true));
    es.addEventListener("offer_new", (ev) => {
      try {
        const data = JSON.parse(ev.data);
        toast.success(`New shift offer: ${data.offer?.job_title} (${data.offer?.distance_km} km)`, { duration: 6000 });
        load();
      } catch (err) { devWarn("sse offer_new parse", err); }
    });
    es.addEventListener("offer_cancelled", () => load());
    es.addEventListener("offer_expired", () => load());
    es.addEventListener("offer_accepted", () => load());
    es.onerror = () => {
      if (closed) return;
      setConnected(false);
    };
    return () => { closed = true; es.close(); };
  }, [load]);

  const accept = async (o) => {
    setBusy(true);
    try {
      const r = await api.post(`/staff/offers/${o.id}/accept`, {});
      toast.success("Accepted — booking confirmed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept");
      load();
    } finally { setBusy(false); }
  };
  const decline = async (o) => {
    setBusy(true);
    try {
      await api.post(`/staff/offers/${o.id}/decline`, {});
      toast("Declined");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not decline"); }
    finally { setBusy(false); }
  };

  const pending = offers.filter((o) => o.status === "pending");
  const past = offers.filter((o) => o.status !== "pending");

  return (
    <div className="space-y-5" data-testid="staff-offers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
            <Broadcast weight="fill" className="text-emerald-600" size={30}/> Live offers
          </h1>
          <div className="text-sm text-slate-600 mt-1">
            {connected ? <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>Live · listening for offers</span> : <span className="text-amber-700">Reconnecting…</span>}
          </div>
        </div>
        {!locOk && (
          <Button data-testid="offers-share-location" onClick={shareLocation} className="bg-blue-600 hover:bg-blue-700">
            <MapPin size={16} className="mr-1.5"/> Share my location
          </Button>
        )}
      </div>

      {pending.length === 0 && (
        <div className="card-tactile p-8 text-center text-slate-500">
          <Broadcast size={40} className="mx-auto mb-2 text-slate-300"/>
          <div className="font-semibold text-slate-700">No live offers right now</div>
          <div className="text-sm mt-1">You’ll see shifts appear here in real time when a company goes live.</div>
        </div>
      )}
      <div className="space-y-3">
        {pending.map((o) => <OfferCard key={o.id} o={o} onAccept={accept} onDecline={decline} busy={busy}/>)}
      </div>

      {past.length > 0 && (
        <>
          <div className="label-caps text-slate-500 pt-4">Recent offers</div>
          <div className="space-y-2">
            {past.map((o) => <OfferCard key={o.id} o={o} onAccept={accept} onDecline={decline} busy={busy}/>)}
          </div>
        </>
      )}
    </div>
  );
}
